import { Matrix } from './sylvester.src';

/**
 * Computes singular value decomposition (SVD) of Matrix X
 * Works with sylvester linear algebra library
 * @param {Matrix} X 2d Matrix
 * @returns {{U:Matrix, S:Matrix, V:Matrix}} Singular value decomposition of X
 */
export function SVD(X) {
  const dimensions = X.dimensions();
  let m = dimensions.rows;
  let n = dimensions.cols;
  let temp;
  let precision = Math.pow(2, -52);
  let tolerance = 1e-64 / precision;
  let iterMax = 50;
  let c = 0;
  let i = 0;
  let j = 0;
  let k = 0;
  let l = 0;

  let U = X.clone();
  if (m < n) throw `Need more rows than columns`;

  let e = new Float32Array(n).fill(0);
  const S = Matrix.Zero(n, n);
  const V = new Matrix.Zero(n, n);

  function pythag(a, b) {
    a = Math.abs(a);
    b = Math.abs(b);
    if (a > b) {
      return a * Math.sqrt(1.0 + (b * b) / a / a);
    } else if (b == 0.0) {
      return a;
    }
    return b * Math.sqrt(1.0 + (a * a) / b / b);
  }

  // Householder's reduction to bidiagonal form
  let f = 0.0;
  let g = 0.0;
  let h = 0.0;
  let x = 0.0;
  let y = 0.0;
  let z = 0.0;
  let s = 0.0;

  for (i = 0; i < n; i++) {
    e[i] = g;
    s = 0.0;
    l = i + 1;
    for (j = i; j < m; j++) {
      s += U.get(j, i) ** 2;
    }
    if (s <= tolerance) {
      g = 0.0;
    } else {
      f = U.get(i, i);
      g = Math.sqrt(s);
      if (f >= 0.0) {
        g = -g;
      }
      h = f * g - s;
      U.set(i, i, f - g);
      for (j = l; j < n; j++) {
        s = 0.0;
        for (k = i; k < m; k++) {
          s += U.get(k, i) * U.get(k, j);
        }
        f = s / h;
        for (k = i; k < m; k++) {
          U.set(k, j, U.get(k, j) + f * U.get(k, i));
        }
      }
    }
    S.set(i, i, g);
    s = 0.0;
    for (j = l; j < n; j++) {
      s += U.get(i, j) ** 2;
    }
    if (s <= tolerance) {
      g = 0.0;
    } else {
      f = U.get(i, i + 1);
      g = Math.sqrt(s);
      if (f >= 0.0) {
        g = -g;
      }
      h = f * g - s;
      U.set(i, i + 1, f - g);
      for (j = l; j < n; j++) {
        e[j] = U.get(i, j) / h;
      }
      for (j = l; j < m; j++) {
        s = 0.0;
        for (k = l; k < n; k++) {
          s += U.get(j, k) * U.get(i, k);
        }
        for (k = l; k < n; k++) {
          U.set(j, k, U.get(j, k) + s * e[k]);
        }
      }
    }
    y = Math.abs(S.get(i, i)) + Math.abs(e[i]);
    if (y > x) {
      x = y;
    }
  }

  // Right hand transformations
  for (i = n - 1; i != -1; i += -1) {
    if (g != 0.0) {
      h = g * U.get(i, i + 1);
      for (j = l; j < n; j++) {
        V.set(j, i, U.get(i, j) / h);
      }
      for (j = l; j < n; j++) {
        s = 0.0;
        for (k = l; k < n; k++) {
          s += U.get(i, k) * V.get(k, j);
        }
        for (k = l; k < n; k++) {
          V.set(k, j, V.get(k, j) + s * V.get(k, i));
        }
      }
    }
    for (j = l; j < n; j++) {
      V.set(i, j, 0);
      V.set(j, i, 0);
    }
    V.set(i, i, 1);
    g = e[i];
    l = i;
  }

  // Left hand transformations
  for (i = n - 1; i != -1; i += -1) {
    l = i + 1;
    g = S.get(i, i);
    for (j = l; j < n; j++) U.set(i, j, 0);
    if (g != 0.0) {
      h = U.get(i, i) * g;
      for (j = l; j < n; j++) {
        s = 0.0;
        for (k = l; k < m; k++) {
          s += U.get(k, i) * U.get(k, j);
        }
        f = s / h;
        for (k = i; k < m; k++) {
          U.set(k, j, U.get(k, j) + f * U.get(k, i));
        }
      }
      for (j = i; j < m; j++) {
        U.set(j, i, U.get(j, i) / g);
      }
    } else
      for (j = i; j < m; j++) {
        U.set(j, i, 0);
      }
    U.set(i, i, U.get(i, i) + 1);
  }

  // diagonalization of the bidiagonal form
  precision = precision * x;
  for (k = n - 1; k != -1; k += -1) {
    for (var iteration = 0; iteration < iterMax; iteration++) {
      // test f splitting
      var test_convergence = false;
      for (l = k; l != -1; l += -1) {
        if (Math.abs(e[l]) <= precision) {
          test_convergence = true;
          break;
        }
        if (Math.abs(S.get(l - 1, l - 1)) <= precision) {
          break;
        }
      }
      if (!test_convergence) {
        // cancellation of e[l] if l>0
        c = 0.0;
        s = 1.0;
        var l1 = l - 1;
        for (i = l; i < k + 1; i++) {
          f = s * e[i];
          e[i] = c * e[i];
          if (Math.abs(f) <= precision) {
            break;
          }
          g = S.get(i, i);
          h = pythag(f, g);
          S.set(i, i, h);
          c = g / h;
          s = -f / h;
          for (j = 0; j < m; j++) {
            y = U.get(j, l1);
            z = U.get(j, i);
            U.set(j, l1, y * c + z * s);
            U.set(j, i, -y * s + z * c);
          }
        }
      }
      // test f convergence
      z = S.get(k, k);
      if (l == k) {
        //convergence
        if (z < 0.0) {
          // S[k] is made non-negative
          S.set(k, k, -z);
          for (j = 0; j < n; j++) V.set(j, k, -V.get(j, k));
        }
        break;
      }
      if (iteration >= iterMax - 1) {
        throw 'Error: no convergence.';
      }

      // shift from bottom 2x2 minor
      x = S.get(l, l);
      y = S.get(k - 1, k - 1);
      g = e[k - 1];
      h = e[k];
      f = ((y - z) * (y + z) + (g - h) * (g + h)) / (2.0 * h * y);
      g = pythag(f, 1.0);
      if (f < 0.0) {
        f = ((x - z) * (x + z) + h * (y / (f - g) - h)) / x;
      } else {
        f = ((x - z) * (x + z) + h * (y / (f + g) - h)) / x;
      }

      // next QR transformation
      c = 1.0;
      s = 1.0;
      for (i = l + 1; i < k + 1; i++) {
        g = e[i];
        y = S.get(i, i);
        h = s * g;
        g = c * g;
        z = pythag(f, h);
        e[i - 1] = z;
        c = f / z;
        s = h / z;
        f = x * c + g * s;
        g = -x * s + g * c;
        h = y * s;
        y = y * c;
        for (j = 0; j < n; j++) {
          x = V.get(j, i - 1);
          z = V.get(j, i);
          V.set(j, i - 1, x * c + z * s);
          V.set(j, i, -x * s + z * c);
        }
        z = pythag(f, h);
        S.set(i - 1, i - 1, z);
        c = f / z;
        s = h / z;
        f = c * g + s * y;
        x = -s * g + c * y;
        for (j = 0; j < m; j++) {
          y = U.get(j, i - 1);
          z = U.get(j, i);
          U.set(j, i - 1, y * c + z * s);
          U.set(j, i, -y * s + z * c);
        }
      }
      e[l] = 0.0;
      e[k] = f;
      S.set(k, k, x);
    }
  }

  // Zero out values closer to zero than precision
  for (i = 0; i < S.n; i++) {
    if (S.get(i, i) < precision) {
      S.set(i, i, 0);
    }
  }

  // Sort eigenvalues
  for (i = 0; i < n; i++) {
    for (j = i - 1; j >= 0; j--) {
      if (S.get(j, j) < S.get(i, i)) {
        c = S.get(j, j);
        S.set(j, j, S.get(i, i));
        S.set(i, i, c);
        for (k = 0; k < m; k++) {
          temp = U.get(k, i);
          U.set(k, i, U.get(k, j));
          U.set(k, j, temp);
        }
        for (k = 0; k < n; k++) {
          temp = V.get(k, i);
          V.set(k, i, V.get(k, j));
          V.set(k, j, temp);
        }
        i = j;
      }
    }
  }
  let results = {
    U: U,
    S: S,
    V: V.transpose()
  };

  return results;
}
